import { FormulaireAdmin, type FormProps } from "../admin/ui.tsx";
import { creerEntite } from "../lib/api.ts";
import { useApp } from "../app/state.tsx";

/** Tunnels d'inscription A3–A7 — mêmes gabarits que le back-office (blocs, chercheur, choix). */

export type TunnelKind = "stagiaire" | "visiteur" | "invite" | "intervenant" | "organisateur";

const IDENTITE = [
  { label: "Nom", requis: true, placeholder: "Nom…" },
  { label: "Prénom", requis: true, placeholder: "Prénom…" },
  { label: "Email", requis: true, placeholder: "vous@etablissement.fr" },
  { label: "Téléphone mobile", placeholder: "06 12 34 56 78" },
] as const;

const ETABLISSEMENT = {
  titre: "Établissement",
  finder: true,
  champs: [
    { label: "Nom de l'établissement", requis: true, placeholder: "Nom…" },
    { label: "Code postal", requis: true, placeholder: "Code postal…" },
    { label: "Adresse ligne 1", span: 2 as const, placeholder: "Ligne 1 de l'adresse…" },
    { label: "Ville", requis: true, placeholder: "Ville…" },
    { label: "Pays", type: "select" as const, options: ["France", "Belgique", "Suisse"], requis: true },
  ],
};

const CONFIGS: Record<TunnelKind, FormProps> = {
  stagiaire: {
    intro: "Inscrivez-vous à la formation, ou inscrivez vos collaborateurs. L'établissement se choisit dans le référentiel national.",
    choix: { label: "Qui s'inscrit ?", options: ["Je m'inscris pour moi", "J'inscris un ou des collaborateurs"] },
    submit: "Créer le stagiaire",
    note: "Statut de paiement : accepté (stagiaire effectif) ou en cours (à venir).",
    blocs: [
      { titre: "Informations personnelles", champs: [
        ...IDENTITE,
        { label: "Grade", placeholder: "Grade…" },
        { label: "Spécialité", placeholder: "Spécialité…" },
        { label: "Service", requis: true, placeholder: "Service…" },
      ] },
      ETABLISSEMENT,
      { titre: "Formation", champs: [
        { label: "Formule", type: "select", options: ["Journée complète", "Demi-journée"], span: 2 },
      ] },
    ],
  },
  visiteur: {
    intro: "Préinscription en ligne. Le badge se retire ensuite à l'accueil, sur place.",
    submit: "Enregistrer la préinscription",
    note: "La préinscription accélère le retrait du badge — elle ne le remplace pas.",
    blocs: [
      { titre: "Informations personnelles", champs: [
        ...IDENTITE,
        { label: "Référence de facture à rappeler", span: 2, placeholder: "HOPITECH…" },
      ] },
      { titre: "Coordonnées", finder: true, champs: [
        { label: "Établissement", requis: true, placeholder: "Nom de l'établissement…" },
        { label: "Code postal", requis: true, placeholder: "Code postal…" },
        { label: "Adresse ligne 1", span: 2, placeholder: "Ligne 1 de l'adresse…" },
        { label: "Ville", requis: true, placeholder: "Ville…" },
        { label: "Pays", type: "select", options: ["France", "Belgique", "Suisse"] },
      ] },
      { titre: "Tarif", champs: [
        { label: "Choix du tarif", type: "select", span: 2, requis: true, options: [
          "Hospitalier — Journée du jeudi avec déjeuner (30,00 € HT)",
          "Hospitalier — Journée du vendredi avec déjeuner (30,00 € HT)",
          "Hospitalier — Journée sans déjeuner (gratuit)",
        ] },
      ] },
    ],
  },
  invite: {
    banniere: (
      <div style={{ display: "flex", alignItems: "center", gap: 13, padding: "15px 18px", borderRadius: 12, background: "var(--soft)", border: "1px solid var(--accent)" }}>
        <span style={{ width: 34, height: 34, borderRadius: 9, background: "var(--surface)", border: "1px solid var(--line)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: "var(--accent)" }}>DEL</span>
        <span style={{ fontSize: 13.5, color: "var(--ink-2)" }}>Invitation envoyée par <strong>DELABIE</strong> — stand A-14. Votre inscription est offerte par l'exposant.</span>
      </div>
    ),
    intro: "Vous êtes invité par un exposant. Renseignez vos coordonnées ; le badge se retire à l'accueil.",
    submit: "Confirmer mon inscription",
    blocs: [
      { titre: "Informations personnelles", champs: [...IDENTITE] },
      { titre: "Coordonnées", champs: [
        { label: "Établissement", requis: true, placeholder: "Nom…" },
        { label: "Ville", requis: true, placeholder: "Ville…" },
      ] },
    ],
  },
  intervenant: {
    intro: "Enregistrez votre intervention et vos coordonnées.",
    submit: "Enregistrer les informations",
    blocs: [
      { titre: "Informations personnelles", champs: [
        { label: "Nom", requis: true, placeholder: "Nom…" },
        { label: "Prénom", requis: true, placeholder: "Prénom…" },
        { label: "Fonction", requis: true, placeholder: "Fonction…" },
        { label: "Service", placeholder: "Service…" },
        { label: "Email", requis: true, placeholder: "vous@etablissement.fr" },
        { label: "Téléphone mobile", placeholder: "06 12 34 56 78" },
      ] },
      { ...ETABLISSEMENT, finder: false },
    ],
  },
  organisateur: {
    intro: "Membre du comité d'organisation.",
    submit: "Enregistrer les informations",
    blocs: [
      { titre: "Informations personnelles", champs: [
        { label: "Nom", requis: true, placeholder: "Nom…" },
        { label: "Prénom", requis: true, placeholder: "Prénom…" },
        { label: "Fonction", requis: true, placeholder: "Fonction…" },
        { label: "Email", requis: true, placeholder: "vous@etablissement.fr" },
      ] },
      ETABLISSEMENT,
    ],
  },
};

export function TunnelForm({ kind }: { kind: TunnelKind }): React.ReactElement {
  const app = useApp();
  const enregistrer = async (valeurs: Record<string, string>): Promise<void> => {
    await creerEntite(kind, app.edition.id, valeurs);
  };
  return <FormulaireAdmin {...CONFIGS[kind]} onSubmit={enregistrer} />;
}
